import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Radio,
  IconButton,
  Button,
  Divider,
  InputAdornment,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Add, DeleteOutline } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState, useEffect, useRef } from "react";
import TextInput from "../../../../components/textinput/textinput";
import InputNumber from "../../../../components/input-number/input-number";
import { useDispatch, useSelector } from "react-redux";
import {
  getCountriesListRequest,
  getStatesByCountryRequest,
  resetLocationsData,
} from "../../../../redux/locations/slice";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  phoneFormatInput,
  normalizePropertyContactPhoneType,
  propertyContactPhoneTypeForApi,
  propertyContactEmailForApi,
} from "../../../../utils/Formatter";
import {
  propertyDetailsTypes,
  assetManagerDetailTypes,
  additionalDetailTypeMap,
} from "../../../../utils/Constant";
import { mapPropertyRecordToOptionalDetails } from "../../../../utils/PropertyDetailsMapper";
import OptionalPropertyDetails from "./optional-property-details";

interface PropertyFormProps {
  mode: "add" | "edit";
  propertyData?: any;
  onSubmit: (data: any) => void;
  loading?: boolean;
  clientId?: string;
  /** When true, only show design fields: Loan #, Property Name, Address, Property Contact. Hidden fields remain in state and submit payload. */
  simplifiedView?: boolean;
}

interface PropertyContact {
  id?: string;
  name: string;
  email: string;
  phone: string;
  phoneType: string;
  isPrimary: boolean;
}

interface OptionalPropertyDetailsType {
  type: string;
  description: string;
}

const validationSchema = Yup.object({
  loanNumber: Yup.string().required("This field can't be empty"),
  street: Yup.string().required("This field can't be empty"),
  city: Yup.string().required("This field can't be empty"),
  state: Yup.string().required("This field can't be empty"),
  zip: Yup.string().required("This field can't be empty"),
  country: Yup.string().required("This field can't be empty"),
  contacts: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().required("This field can't be empty"),
        email: Yup.string()
          .nullable()
          .email("Invalid email format"),
        phone: Yup.string()
          .required("This field can't be empty")
          .matches(/^\(\d{3}\) \d{3}-\d{4}$/, "Invalid phone format"),
        phoneType: Yup.string(),
      })
    )
    .min(1, "At least one contact is required"),
  optionalPropertyDetails: Yup.array().of(
    Yup.object({
      type: Yup.string().required("This field can't be empty"),
      description: Yup.string().required("This field can't be empty"),
    })
  ),
  accountNumber: Yup.string(),
  referenceNumber: Yup.string(),
  propertyName: Yup.string(),
  collateralOrPropertyType: Yup.string(),
  description: Yup.string(),
  yearBuilt: Yup.number()
    .min(1800, "Year must be after 1800")
    .max(new Date().getFullYear(), "Year cannot be in the future"),
  yearRenovated: Yup.number()
    .min(1800, "Year must be after 1800")
    .max(new Date().getFullYear(), "Year cannot be in the future"),
  nameOfBusiness: Yup.string(),
  businessPhone: Yup.string(),
  address2: Yup.string(),
  numberOfUnits: Yup.number().min(1, "Must be at least 1"),
  numberOfBuildings: Yup.number().min(1, "Must be at least 1"),
  investorNumber: Yup.string(),
  squareFootage: Yup.number().min(1, "Must be at least 1"),
  operationsAndMaintenanceRequired: Yup.boolean(),
  loanBalance: Yup.number().min(0, "Must be 0 or greater"),
  loanBalanceAsOf: Yup.string(),
});

export default function PropertyForm({
  propertyData,
  onSubmit,
  clientId = "",
  simplifiedView = false,
}: PropertyFormProps) {
  const [propertyStates, setPropertyStates] = useState<string[]>([]);
  const [contacts, setContacts] = useState<PropertyContact[]>(() => {
    if (propertyData?.contacts && propertyData.contacts.length > 0) {
      const mappedContacts = propertyData.contacts.map(
        (contact: any, index: number) => ({
          id: contact.id || `contact_${Date.now()}_${index}`,
          name: contact.name || "",
          email: contact.email || "",
          phone: contact.phone || "",
          phoneType: normalizePropertyContactPhoneType(contact.phoneType),
          isPrimary: contact.isPrimary,
        })
      );

      return mappedContacts.sort(
        (a: { isPrimary: any }, b: { isPrimary: any }) => {
          if (a.isPrimary && !b.isPrimary) return -1;
          if (!a.isPrimary && b.isPrimary) return 1;
          return 0;
        }
      );
    }
    return [
      {
        id: `contact_${Date.now()}_0`,
        name: "",
        email: "",
        phone: "",
        phoneType: "",
        isPrimary: true,
      },
    ];
  });
  const [optionalPropertyDetails, setOptionalPropertyDetails] = useState<
    OptionalPropertyDetailsType[]
  >([]);

  const dispatch = useDispatch();
  const prevCountryRef = useRef<string | undefined>(propertyData?.country);

  useEffect(() => {
    dispatch(resetLocationsData());
    dispatch(getCountriesListRequest());
    if (!propertyData?.country) {
      setPropertyStates([]);
    }
  }, []); //eslint-disable-line

  const { countries, states } = useSelector((state: any) => state.location);

  const [selectedContact, setSelectedContact] = useState(() => {
    if (propertyData?.contacts && propertyData.contacts.length > 0) {
      const primaryIndex = propertyData.contacts.findIndex(
        (contact: any) => contact.isPrimary
      );
      return `contact${primaryIndex >= 0 ? primaryIndex : 0}`;
    }
    return "contact0";
  });

  const formik = useFormik({
    initialValues: {
      loanNumber: propertyData?.loanNumber || "",
      accountNumber: propertyData?.accountNumber || "",
      referenceNumber: propertyData?.referenceNumber || "",
      propertyName: propertyData?.propertyName || "",
      collateralOrPropertyType: propertyData?.collateralOrPropertyType || "",
      collateralOrPropertyDescription:
        propertyData?.collateralOrPropertyDescription || "",
      yearBuilt: propertyData?.yearBuilt || "",
      yearRenovated: propertyData?.yearRenovated || "",
      nameOfBusiness: propertyData?.nameOfBusiness || "",
      businessPhone: propertyData?.businessPhone || "",
      country: propertyData?.country || "",
      street: propertyData?.street || "",
      address2: propertyData?.address2 || "",
      city: propertyData?.city || "",
      state: propertyData?.state || "",
      zip: propertyData?.zip || "",
      numberOfUnits: propertyData?.numberOfUnits || "",
      numberOfBuildings: propertyData?.numberOfBuildings || "",
      investorNumber: propertyData?.investorNumber || "",
      squareFootage: propertyData?.squareFootage || "",
      operationsAndMaintenanceRequired:
        propertyData?.operationsAndMaintenanceRequired || false,
      loanBalance: propertyData?.loanBalance || "",
      loanBalanceAsOf: propertyData?.loanBalanceAsOf || "",
      contacts: contacts,
      optionalPropertyDetails: optionalPropertyDetails,
    },
    validationSchema,
    onSubmit: (values) => {
      const apiPayload = transformToApiPayload(values);
      onSubmit(apiPayload);
    },
  });

  const addContact = () => {
    const newContacts = [
      ...contacts,
      {
        id: `contact_${Date.now()}_${contacts.length}`,
        name: "",
        email: "",
        phone: "",
        phoneType: "",
        isPrimary: false,
      },
    ];

    setContacts(newContacts);
    formik.setFieldValue("contacts", newContacts);
    formik.setFieldTouched("contacts", true);

    setTimeout(() => {
      formik.validateField("contacts");
    }, 0);
  };

  const addOptionalDetails = () => {
    const newOptionalDetails = [
      ...optionalPropertyDetails,
      {
        type: "",
        description: "",
      },
    ];
    setOptionalPropertyDetails(newOptionalDetails);
    formik.setFieldValue("optionalPropertyDetails", newOptionalDetails);
    formik.setFieldTouched("optionalPropertyDetails", true);
    setTimeout(() => {
      formik.validateField("optionalPropertyDetails");
    }, 0);
  };

  const updateOptionalDetailsType = (value: string, index: number) => {
    const newOptionalDetails = optionalPropertyDetails.map((item, i) =>
      i === index ? { ...item, type: value } : item
    );
    setOptionalPropertyDetails(newOptionalDetails);
    formik.setFieldValue("optionalPropertyDetails", newOptionalDetails);
  };

  const updateOptionalDetailsDescription = (value: string, index: number) => {
    const newOptionalDetails = optionalPropertyDetails.map((item, i) =>
      i === index ? { ...item, description: value } : item
    );
    setOptionalPropertyDetails(newOptionalDetails);
    formik.setFieldValue("optionalPropertyDetails", newOptionalDetails);
  };

  const removeOptionalDetails = (removeIndex: number) => {
    const newOptionalDetails = optionalPropertyDetails.filter(
      (_, i) => i !== removeIndex
    );
    setOptionalPropertyDetails(newOptionalDetails);
    formik.setFieldValue("optionalPropertyDetails", newOptionalDetails);
    formik.setFieldTouched("optionalPropertyDetails", true);

    setTimeout(() => {
      formik.validateField("optionalPropertyDetails");
    }, 0);
  };

  useEffect(() => {
    if (formik.values.country) {
      dispatch(getStatesByCountryRequest({ country: formik.values.country }));
      if (
        prevCountryRef.current !== undefined &&
        prevCountryRef.current !== formik.values.country
      ) {
        formik.setFieldValue("state", "");
        setPropertyStates([]);
      }
      prevCountryRef.current = formik.values.country;
    } else {
      setPropertyStates([]);
      formik.setFieldValue("state", "");
      prevCountryRef.current = undefined;
    }
  }, [formik.values.country]); //eslint-disable-line

  useEffect(() => {
    if (states && states?.country === formik.values.country && states?.states) {
      setPropertyStates(states.states);
    } else if (!formik.values.country) {
      setPropertyStates([]);
    }
  }, [states, formik.values.country]);

  const removeContact = (index: number) => {
    if (contacts.length > 1) {
      const newContacts = contacts.filter((_, i) => i !== index);

      if (contacts[index].isPrimary && newContacts.length > 0) {
        newContacts[0].isPrimary = true;
        setSelectedContact("contact0");
      } else {
        const selectedIndex = Number(
          selectedContact.substring(selectedContact.length - 1)
        );
        if (selectedIndex > index) {
          setSelectedContact(`contact${selectedIndex - 1}`);
        }
      }

      setContacts(newContacts);
      formik.setFieldValue("contacts", newContacts);
      formik.setFieldTouched("contacts", true);

      setTimeout(() => {
        formik.validateField("contacts");
      }, 0);
    }
  };

  const handleSelectedContactChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    selectedIndex: number
  ) => {
    setSelectedContact(event.target.value);
    const newContacts = [...contacts];
    newContacts.forEach((contact, index) => {
      contact.isPrimary = index === selectedIndex;
    });

    setContacts(newContacts);
    formik.setFieldValue("contacts", newContacts);

    setTimeout(() => {
      formik.validateField("contacts");
    }, 0);
  };

  const updateContact = (
    index: number,
    field: keyof PropertyContact,
    value: any
  ) => {
    const newContacts = [...contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };

    if (field === "isPrimary" && value === true) {
      newContacts.forEach((contact, idx) => {
        if (idx !== index) {
          contact.isPrimary = false;
        }
      });
    }

    setContacts(newContacts);
    formik.setFieldValue("contacts", newContacts);
  };

  const normalizePhone = (phone: string) => phone.replace(/\D/g, "");

  const validatePhones = () => {
    const phoneErrors: string[] = [];
    const normalizedToIndex: Record<string, number> = {};
    contacts.forEach((contact, idx) => {
      if (!contact.phone) {
        phoneErrors[idx] = "";
      } else {
        phoneErrors[idx] = "";
        const normalized = normalizePhone(contact.phone);
        if (normalized && normalizedToIndex.hasOwnProperty(normalized)) {
          phoneErrors[idx] = "Duplicate phone not allowed.";
        } else {
          normalizedToIndex[normalized] = idx;
        }
      }
    });
    if (formik.submitCount > 0) {
      contacts.forEach((contact, idx) => {
        if (!contact.phone) {
          phoneErrors[idx] = "This field can't be empty";
        }
      });
    }
    const primaryCount = contacts.filter((contact) => contact.isPrimary).length;
    if (primaryCount !== 1) {
      contacts.forEach((_, idx) => {
        phoneErrors[idx] =
          primaryCount === 0
            ? "Please select a primary phone number."
            : primaryCount > 1 && contacts[idx].isPrimary
            ? "Only one phone can be primary."
            : phoneErrors[idx];
      });
    }
    return phoneErrors;
  };

  useEffect(() => {
    if (propertyData?.contacts && propertyData.contacts.length > 0) {
      const mappedContacts = propertyData.contacts.map(
        (contact: any, index: number) => ({
          id: contact.id || `contact_${Date.now()}_${index}`,
          name: contact.name || "",
          email: contact.email || "",
          phone: contact.phone || "",
          phoneType: normalizePropertyContactPhoneType(contact.phoneType),
          isPrimary: contact.isPrimary === true,
        })
      );

      const sortedContacts = mappedContacts.sort(
        (a: PropertyContact, b: PropertyContact) => {
          if (a.isPrimary && !b.isPrimary) return -1;
          if (!a.isPrimary && b.isPrimary) return 1;
          return 0;
        }
      );

      setContacts(sortedContacts);

      const primaryIndex = sortedContacts.findIndex(
        (contact: PropertyContact) => contact.isPrimary
      );
      setSelectedContact(`contact${primaryIndex >= 0 ? primaryIndex : 0}`);

      formik.setFieldValue("contacts", sortedContacts);
    }

    if (propertyData) {
      const mappedPropertyDetails =
        mapPropertyRecordToOptionalDetails(propertyData);
      setOptionalPropertyDetails(mappedPropertyDetails);
      formik.setFieldValue("optionalPropertyDetails", mappedPropertyDetails);
      formik.setFieldTouched("optionalPropertyDetails", true);
      setTimeout(() => {
        formik.validateField("optionalPropertyDetails");
      }, 0);
    }
  }, [propertyData]); //eslint-disable-line

  const transformToApiPayload = (formData: any) => {
    const payload: any = {
      clientId: clientId || "",
      street: formData.street || "",
      city: formData.city || "",
      state: formData.state || "",
      country: formData.country || "",
      zip: formData.zip || "",
      loanNumber: formData.loanNumber || "",
      contacts:
        formData.contacts?.map((contact: PropertyContact) => ({
          id: contact.id || "",
          name: contact.name || "",
          email: propertyContactEmailForApi(contact.email),
          phone: contact.phone || "",
          phoneType: propertyContactPhoneTypeForApi(contact.phoneType),
          isPrimary: contact.isPrimary || false,
        })) || [],
    };

    if (formData.propertyName) payload.propertyName = formData.propertyName;
    if (formData.address2) payload.address2 = formData.address2;

    // Sibling structures on the PropertyRecord payload.
    const assetManager: Record<string, string> = {};
    const additionalDetails: Array<{
      type: "OperationsAndMaintenance" | "Repair";
      index: number;
      value: string;
    }> = [];
    propertyDetailsTypes?.forEach((types) => {
      optionalPropertyDetails?.forEach((details) => {
        if (details?.type === types?.value) {
          if (types?.value in assetManagerDetailTypes) {
            // Asset Manager rows collapse into the assetManager object.
            const key =
              assetManagerDetailTypes[
                types?.value as keyof typeof assetManagerDetailTypes
              ];
            assetManager[key] = details?.description;
          } else if (types?.value in additionalDetailTypeMap) {
            // O&M / Repair rows map into the additionalDetails array.
            const { type: enumType, index } =
              additionalDetailTypeMap[types?.value];
            additionalDetails.push({
              type: enumType,
              index,
              value: details?.description,
            });
          } else if (
            types?.value === "yearBuilt" ||
            types?.value === "yearRenovated" ||
            types?.value === "numberOfUnits" ||
            types?.value === "numberOfBuildings" ||
            types?.value === "squareFootage"
          ) {
            payload[types?.value] = parseInt(details?.description);
          } else if (types?.value === "loanBalance") {
            payload[types?.value] = parseFloat(details?.description);
          } else if (types?.value === "operationsAndMaintenanceRequired") {
            payload[types?.value] = Boolean(details?.description);
          } else {
            payload[types?.value] = details?.description;
          }
        }
      });
    });
    if (Object.keys(assetManager).length > 0) {
      payload.assetManager = assetManager;
    }
    if (additionalDetails.length > 0) {
      payload.additionalDetails = additionalDetails;
    }
    // if (formData.accountNumber) payload.accountNumber = formData.accountNumber;
    // if (formData.referenceNumber)
    //   payload.referenceNumber = formData.referenceNumber;
    // if (formData.collateralOrPropertyType)
    //   payload.collateralOrPropertyType = formData.collateralOrPropertyType;
    // if (formData.collateralOrPropertyDescription)
    //   payload.collateralOrPropertyDescription =
    //     formData.collateralOrPropertyDescription;
    // if (formData.yearBuilt) payload.yearBuilt = parseInt(formData.yearBuilt);
    // if (formData.yearRenovated)
    //   payload.yearRenovated = parseInt(formData.yearRenovated);
    // if (formData.nameOfBusiness)
    //   payload.nameOfBusiness = formData.nameOfBusiness;
    // if (formData.businessPhone) payload.businessPhone = formData.businessPhone;
    // if (formData.numberOfUnits)
    //   payload.numberOfUnits = parseInt(formData.numberOfUnits);
    // if (formData.numberOfBuildings)
    //   payload.numberOfBuildings = parseInt(formData.numberOfBuildings);
    // if (formData.investorNumber)
    //   payload.investorNumber = formData.investorNumber;
    // if (formData.squareFootage)
    //   payload.squareFootage = parseInt(formData.squareFootage);
    // if (formData.operationsAndMaintenanceRequired !== undefined)
    //   payload.operationsAndMaintenanceRequired =
    //     formData.operationsAndMaintenanceRequired;
    // if (formData.loanBalance)
    //   payload.loanBalance = parseFloat(formData.loanBalance);
    // if (formData.loanBalanceAsOf)
    //   payload.loanBalanceAsOf = formData.loanBalanceAsOf;

    return payload;
  };

  useEffect(() => {
    (window as any).submitPropertyForm = () => {
      formik.handleSubmit();
    };
  }, [formik]);

  const labelAboveSx = {
    fontSize: 12,
    color: "rgba(0, 0, 0, 0.6)",
    mb: 0.5,
  };
  const inputBgSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FAFAFA",
    },
  };

  if (simplifiedView) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box component="form" onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} sx={{ mb: 2, width: "100%" }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={labelAboveSx}>Loan #</Typography>
              <TextInput
                fullWidth
                name="loanNumber"
                variant="outlined"
                size="small"
                value={formik.values.loanNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.loanNumber && Boolean(formik.errors.loanNumber)
                }
                helperText={
                  formik.touched.loanNumber && formik.errors.loanNumber
                    ? String(formik.errors.loanNumber)
                    : ""
                }
                sx={{ width: "100% !important", ...inputBgSx }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={labelAboveSx}>Property Name</Typography>
              <TextInput
                fullWidth
                name="propertyName"
                variant="outlined"
                size="small"
                value={formik.values.propertyName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.propertyName &&
                  Boolean(formik.errors.propertyName)
                }
                helperText={
                  formik.touched.propertyName && formik.errors.propertyName
                    ? String(formik.errors.propertyName)
                    : ""
                }
                sx={{ width: "100% !important", ...inputBgSx }}
              />
            </Grid>
          </Grid>

          <Typography sx={{ ...labelAboveSx, fontWeight: 600, mt: 2, mb: 2 }}>
            Address
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
            <Box>
              <FormControl fullWidth size="small" sx={inputBgSx}>
                <InputLabel>Country</InputLabel>
                <Select
                  name="country"
                  value={formik.values.country}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.country && Boolean(formik.errors.country)
                  }
                  label="Country"
                >
                  {countries?.map((country: any) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.country && formik.errors.country && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                    {String(formik.errors.country)}
                  </Typography>
                )}
              </FormControl>
            </Box>
            <TextInput
              fullWidth
              name="street"
              label="Street Address"
              variant="outlined"
              size="small"
              value={formik.values.street}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.street && Boolean(formik.errors.street)}
              helperText={
                formik.touched.street && formik.errors.street
                  ? String(formik.errors.street)
                  : ""
              }
              sx={{ width: "100% !important", ...inputBgSx }}
            />
            <TextInput
              fullWidth
              name="address2"
              label="Address 2 (Optional)"
              variant="outlined"
              size="small"
              value={formik.values.address2}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              sx={{ width: "100% !important", ...inputBgSx }}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography sx={labelAboveSx}>City</Typography>
                <TextInput
                  fullWidth
                  name="city"
                  variant="outlined"
                  size="small"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={
                    formik.touched.city && formik.errors.city
                      ? String(formik.errors.city)
                      : ""
                  }
                  sx={{ width: "100% !important", ...inputBgSx }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography sx={labelAboveSx}>State</Typography>
                <FormControl fullWidth size="small" sx={inputBgSx}>
                  <InputLabel>State</InputLabel>
                  <Select
                    name="state"
                    value={formik.values.state}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.state && Boolean(formik.errors.state)}
                    label="State"
                  >
                    {propertyStates?.map((state: any) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.state && formik.errors.state && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ mt: 0.5 }}
                    >
                      {String(formik.errors.state)}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography sx={labelAboveSx}>Zip</Typography>
                <TextInput
                  fullWidth
                  name="zip"
                  variant="outlined"
                  size="small"
                  value={formik.values.zip}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.zip && Boolean(formik.errors.zip)}
                  helperText={
                    formik.touched.zip && formik.errors.zip
                      ? String(formik.errors.zip)
                      : ""
                  }
                  sx={{ width: "100% !important", ...inputBgSx }}
                />
              </Grid>
            </Grid>
          </Box>

          <Typography sx={{ ...labelAboveSx, fontWeight: 600, mt: 2, mb: 1 }}>
            Property Contact
          </Typography>
          <Typography sx={{ ...labelAboveSx, mb: 1 }}>Primary</Typography>
          {contacts.map((contact, index) => (
            <Box
              key={index}
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                gridTemplateRows: "auto auto",
                gap: 2,
                mb: 2,
                alignItems: "start",
              }}
            >
              <Box sx={{ gridRow: 1, gridColumn: 1, pt: 0.5 }}>
                <Radio
                  sx={{ color: "primary.main", p: 0 }}
                  checked={selectedContact === `contact${index}`}
                  onChange={(e) => handleSelectedContactChange(e, index)}
                  value={`contact${index}`}
                  name="contact-radio"
                />
              </Box>
              <Box
                sx={{ gridRow: 1, gridColumn: 2, minWidth: 0, width: "100%" }}
              >
                <TextInput
                  name={`contact_${index}_name`}
                  label="Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={contact.name}
                  onChange={(e) => updateContact(index, "name", e.target.value)}
                  onBlur={() => {
                    formik.setFieldTouched(`contacts.${index}.name`, true);
                    setTimeout(
                      () => formik.validateField(`contacts.${index}.name`),
                      0
                    );
                  }}
                  error={Boolean(
                    (formik.touched.contacts?.[index] as any)?.name &&
                      (formik.errors.contacts?.[index] as any)?.name
                  )}
                  helperText={
                    (formik.touched.contacts?.[index] as any)?.name &&
                    (formik.errors.contacts?.[index] as any)?.name
                      ? String((formik.errors.contacts?.[index] as any)?.name)
                      : ""
                  }
                  sx={{ width: "100% !important", ...inputBgSx }}
                />
              </Box>
              <Box
                sx={{
                  gridRow: 1,
                  gridColumn: 3,
                  display: "flex",
                  justifyContent: "flex-end",
                  pt: 0.5,
                  width: "40px",
                }}
              >
                {!contact.isPrimary ? (
                  <IconButton
                    onClick={() => removeContact(index)}
                    sx={{ color: "error.main", p: 0.5, mr: 1 }}
                    size="small"
                    aria-label="delete contact"
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                ) : null}
              </Box>
              <Box sx={{ gridRow: 2, gridColumn: 1 }} />
              <Box
                sx={{
                  gridRow: 2,
                  gridColumn: 2,
                  display: "grid",
                  gridTemplateColumns: "2fr 2fr 1fr",
                  gap: 2,
                  minWidth: 0,
                }}
              >
                <TextInput
                  name={`contact_${index}_email`}
                  label="Email"
                  variant="outlined"
                  size="small"
                  type="email"
                  value={contact.email}
                  onChange={(e) =>
                    updateContact(index, "email", e.target.value)
                  }
                  onBlur={() => {
                    formik.setFieldTouched(`contacts.${index}.email`, true);
                    setTimeout(
                      () => formik.validateField(`contacts.${index}.email`),
                      0
                    );
                  }}
                  error={Boolean(
                    (formik.touched.contacts?.[index] as any)?.email &&
                      (formik.errors.contacts?.[index] as any)?.email
                  )}
                  helperText={
                    (formik.touched.contacts?.[index] as any)?.email &&
                    (formik.errors.contacts?.[index] as any)?.email
                      ? String((formik.errors.contacts?.[index] as any)?.email)
                      : ""
                  }
                  fullWidth
                  style={{ width: "100%" }}
                  sx={{ minWidth: 0, ...inputBgSx }}
                />
                <TextInput
                  name={`contact_${index}_phone`}
                  label="Phone"
                  variant="outlined"
                  size="small"
                  value={contact.phone}
                  onChange={(e) =>
                    updateContact(
                      index,
                      "phone",
                      phoneFormatInput(e.target.value)
                    )
                  }
                  onBlur={() => {
                    formik.setFieldTouched(`contacts.${index}.phone`, true);
                    setTimeout(
                      () => formik.validateField(`contacts.${index}.phone`),
                      0
                    );
                  }}
                  error={
                    Boolean(
                      (formik.touched.contacts?.[index] as any)?.phone &&
                        (formik.errors.contacts?.[index] as any)?.phone
                    ) || Boolean(validatePhones()[index])
                  }
                  helperText={
                    (formik.touched.contacts?.[index] as any)?.phone &&
                    (formik.errors.contacts?.[index] as any)?.phone
                      ? String((formik.errors.contacts?.[index] as any)?.phone)
                      : validatePhones()[index] || ""
                  }
                  fullWidth
                  style={{ width: "100%" }}
                  sx={{ minWidth: 0, ...inputBgSx }}
                />
                <FormControl
                  sx={{ width: "100%", minWidth: 0, ...inputBgSx }}
                  size="small"
                >
                  <InputLabel
                    id={`property-contact-phone-type-label-${index}`}
                    shrink
                  >
                    Phone Type
                  </InputLabel>
                  <Select
                    displayEmpty
                    labelId={`property-contact-phone-type-label-${index}`}
                    value={contact.phoneType ?? ""}
                    onChange={(e) =>
                      updateContact(index, "phoneType", e.target.value)
                    }
                    onBlur={() => {
                      formik.setFieldTouched(
                        `contacts.${index}.phoneType`,
                        true
                      );
                      setTimeout(
                        () =>
                          formik.validateField(`contacts.${index}.phoneType`),
                        0
                      );
                    }}
                    label="Phone Type"
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    <MenuItem value="Mobile">Mobile</MenuItem>
                    <MenuItem value="Office">Office</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ gridRow: 2, gridColumn: 3 }} />
            </Box>
          ))}
          {contacts.length < 4 && (
            <Button
              startIcon={<Add sx={{ width: 18, height: 18 }} />}
              onClick={addContact}
              sx={{
                color: "primary.main",
                fontWeight: 13,
                alignItems: "center",
                "&:hover": { backgroundColor: "transparent" },
              }}
            >
              <b>ADD ADDITIONAL</b>
            </Button>
          )}

          <OptionalPropertyDetails
            optionalPropertyDetails={optionalPropertyDetails}
            formik={formik}
            addOptionalDetails={addOptionalDetails}
            updateOptionalDetailsType={updateOptionalDetailsType}
            updateOptionalDetailsDescription={updateOptionalDetailsDescription}
            removeOptionalDetails={removeOptionalDetails}
          />
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box component="form" onSubmit={formik.handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <Box className="user-name-layout">
            <Typography className="field-label-txt">Loan #</Typography>
            <TextInput
              className="field-input"
              name="loanNumber"
              variant="outlined"
              value={formik.values.loanNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.loanNumber && Boolean(formik.errors.loanNumber)
              }
              helperText={
                formik.touched.loanNumber && formik.errors.loanNumber
                  ? String(formik.errors.loanNumber)
                  : ""
              }
              sx={{ width: "calc(100% - 180px)" }}
            />
          </Box>
          <>
              <Divider />
              <Box className="user-name-layout">
                <Typography className="field-label-txt">Account #</Typography>
                <TextInput
                  className="field-input"
                  name="accountNumber"
                  variant="outlined"
                  value={formik.values.accountNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.accountNumber &&
                    Boolean(formik.errors.accountNumber)
                  }
                  helperText={
                    formik.touched.accountNumber && formik.errors.accountNumber
                      ? String(formik.errors.accountNumber)
                      : ""
                  }
                  sx={{ width: "calc(100% - 180px)" }}
                />
              </Box>
              <Divider />
              <Box className="user-name-layout">
                <Typography className="field-label-txt">Reference #</Typography>
                <TextInput
                  className="field-input"
                  name="referenceNumber"
                  variant="outlined"
                  value={formik.values.referenceNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.referenceNumber &&
                    Boolean(formik.errors.referenceNumber)
                  }
                  helperText={
                    formik.touched.referenceNumber &&
                    formik.errors.referenceNumber
                      ? String(formik.errors.referenceNumber)
                      : ""
                  }
                  sx={{ width: "calc(100% - 180px)" }}
                />
              </Box>
          </>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="field-label-txt">Property Name</Typography>
            <TextInput
              className="field-input"
              name="propertyName"
              variant="outlined"
              value={formik.values.propertyName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.propertyName &&
                Boolean(formik.errors.propertyName)
              }
              helperText={
                formik.touched.propertyName && formik.errors.propertyName
                  ? String(formik.errors.propertyName)
                  : ""
              }
              sx={{ width: "calc(100% - 180px)" }}
            />
          </Box>
          <>
              <Divider />
              <Box className="user-name-layout">
                <Typography className="field-label-txt">
                  Collateral/Property
                </Typography>
                <Box
                  sx={{
                    width: "calc(100% - 180px)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <TextInput
                    fullWidth
                    className="field-input"
                    name="collateralOrPropertyType"
                    label="Type"
                    variant="outlined"
                    multiline
                    rows={1}
                    value={formik.values.collateralOrPropertyType}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.collateralOrPropertyType &&
                      Boolean(formik.errors.collateralOrPropertyType)
                    }
                    helperText={
                      formik.touched.collateralOrPropertyType &&
                      formik.errors.collateralOrPropertyType
                        ? String(formik.errors.collateralOrPropertyType)
                        : ""
                    }
                    sx={{ width: "100% !important" }}
                  />
                  <TextInput
                    fullWidth
                    className="field-input"
                    name="collateralOrPropertyDescription"
                    label="Description"
                    variant="outlined"
                    multiline
                    rows={1}
                    value={formik.values.collateralOrPropertyDescription}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.collateralOrPropertyDescription &&
                      Boolean(formik.errors.collateralOrPropertyDescription)
                    }
                    helperText={
                      formik.touched.collateralOrPropertyDescription &&
                      formik.errors.collateralOrPropertyDescription
                        ? String(formik.errors.collateralOrPropertyDescription)
                        : ""
                    }
                    sx={{ width: "100% !important", mt: 2 }}
                  />
                </Box>
              </Box>
              <Divider />
              <Box className="user-name-layout">
                <Typography className="field-label-txt">Year Built</Typography>
                <InputNumber
                  className="field-input"
                  name="yearBuilt"
                  label="Year Built"
                  variant="outlined"
                  value={formik.values.yearBuilt}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.yearBuilt && Boolean(formik.errors.yearBuilt)
                  }
                  helperText={
                    formik.touched.yearBuilt && formik.errors.yearBuilt
                      ? String(formik.errors.yearBuilt)
                      : ""
                  }
                  allowDecimal={false}
                  sx={{ width: "calc(100% - 180px)" }}
                />
              </Box>
              <Divider />
              <Box className="user-name-layout">
                <Typography className="field-label-txt">
                  Year Renovated
                </Typography>
                <InputNumber
                  className="field-input"
                  name="yearRenovated"
                  label="Year Renovated"
                  variant="outlined"
                  value={formik.values.yearRenovated}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.yearRenovated &&
                    Boolean(formik.errors.yearRenovated)
                  }
                  helperText={
                    formik.touched.yearRenovated && formik.errors.yearRenovated
                      ? String(formik.errors.yearRenovated)
                      : ""
                  }
                  allowDecimal={false}
                  sx={{ width: "calc(100% - 180px)" }}
                />
              </Box>
              <Divider />
              <Box className="user-name-layout">
                <Typography className="field-label-txt">
                  Name of Business
                </Typography>
                <TextInput
                  className="field-input"
                  name="nameOfBusiness"
                  label="Name of Business"
                  variant="outlined"
                  value={formik.values.nameOfBusiness}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.nameOfBusiness &&
                    Boolean(formik.errors.nameOfBusiness)
                  }
                  helperText={
                    formik.touched.nameOfBusiness &&
                    formik.errors.nameOfBusiness
                      ? String(formik.errors.nameOfBusiness)
                      : ""
                  }
                  sx={{ width: "calc(100% - 180px)" }}
                />
              </Box>
              <Divider />
              <Box className="user-name-layout">
                <Typography className="field-label-txt">
                  Business Phone
                </Typography>
                <TextInput
                  className="field-input"
                  name="businessPhone"
                  label="Business Phone"
                  variant="outlined"
                  value={formik.values.businessPhone}
                  onChange={(e) => {
                    formik.setFieldValue(
                      "businessPhone",
                      phoneFormatInput(e.target.value)
                    );
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.businessPhone &&
                    Boolean(formik.errors.businessPhone)
                  }
                  helperText={
                    formik.touched.businessPhone && formik.errors.businessPhone
                      ? String(formik.errors.businessPhone)
                      : ""
                  }
                  sx={{ width: "calc(100% - 180px)" }}
                />
              </Box>
          </>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="field-label-txt">Address</Typography>
          <Box
            sx={{
              width: "calc(100% - 180px)",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <FormControl>
              <InputLabel>Country</InputLabel>
              <Select
                name="country"
                value={formik.values.country}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.country && Boolean(formik.errors.country)}
                label="Country"
              >
                {countries?.map((country: any) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.country && formik.errors.country && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ mt: 1, ml: 1 }}
                >
                  {String(formik.errors.country)}
                </Typography>
              )}
            </FormControl>
            <TextInput
              className="field-input"
              name="street"
              label="Street Address"
              variant="outlined"
              value={formik.values.street}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.street && Boolean(formik.errors.street)}
              helperText={
                formik.touched.street && formik.errors.street
                  ? String(formik.errors.street)
                  : ""
              }
              sx={{ width: "100% !important" }}
            />

            <TextInput
              className="field-input"
              name="address2"
              label="Address 2 (Optional)"
              variant="outlined"
              value={formik.values.address2}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.address2 && Boolean(formik.errors.address2)}
              helperText={
                formik.touched.address2 && formik.errors.address2
                  ? String(formik.errors.address2)
                  : ""
              }
              sx={{ width: "100% !important" }}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextInput
                sx={{ flex: 1 }}
                className="field-input"
                name="city"
                label="City"
                variant="outlined"
                value={formik.values.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.city && Boolean(formik.errors.city)}
                helperText={
                  formik.touched.city && formik.errors.city
                    ? String(formik.errors.city)
                    : ""
                }
              />
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>State</InputLabel>
                <Select
                  name="state"
                  value={formik.values.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  label="State"
                >
                  {propertyStates?.map((state: any) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.state && formik.errors.state && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, ml: 1 }}
                  >
                    {String(formik.errors.state)}
                  </Typography>
                )}
              </FormControl>
              <TextInput
                sx={{ flex: 1 }}
                className="field-input"
                name="zip"
                label="Zip"
                variant="outlined"
                value={formik.values.zip}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.zip && Boolean(formik.errors.zip)}
                helperText={
                  formik.touched.zip && formik.errors.zip
                    ? String(formik.errors.zip)
                    : ""
                }
              />
            </Box>
          </Box>
        </Box>
        <>
            <Divider />
            <Box className="user-name-layout">
              <Typography className="field-label-txt">
                Number of Units
              </Typography>
              <InputNumber
                className="field-input"
                name="numberOfUnits"
                variant="outlined"
                value={formik.values.numberOfUnits}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.numberOfUnits &&
                  Boolean(formik.errors.numberOfUnits)
                }
                helperText={
                  formik.touched.numberOfUnits && formik.errors.numberOfUnits
                    ? String(formik.errors.numberOfUnits)
                    : ""
                }
                allowDecimal={false}
                sx={{ width: "calc(100% - 180px)" }}
              />
            </Box>
            <Divider />
            <Box className="user-name-layout">
              <Typography className="field-label-txt">
                Number of Buildings
              </Typography>
              <InputNumber
                className="field-input"
                name="numberOfBuildings"
                variant="outlined"
                value={formik.values.numberOfBuildings}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.numberOfBuildings &&
                  Boolean(formik.errors.numberOfBuildings)
                }
                helperText={
                  formik.touched.numberOfBuildings &&
                  formik.errors.numberOfBuildings
                    ? String(formik.errors.numberOfBuildings)
                    : ""
                }
                allowDecimal={false}
                sx={{ width: "calc(100% - 180px)" }}
              />
            </Box>
            <Divider />
            <Box className="user-name-layout">
              <Typography className="field-label-txt">
                Investor Number
              </Typography>
              <TextInput
                className="field-input"
                name="investorNumber"
                variant="outlined"
                value={formik.values.investorNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.investorNumber &&
                  Boolean(formik.errors.investorNumber)
                }
                helperText={
                  formik.touched.investorNumber && formik.errors.investorNumber
                    ? String(formik.errors.investorNumber)
                    : ""
                }
                sx={{ width: "calc(100% - 180px)" }}
              />
            </Box>
            <Divider />
            <Box className="user-name-layout">
              <Typography className="field-label-txt">
                Square Footage
              </Typography>
              <InputNumber
                className="field-input"
                name="squareFootage"
                variant="outlined"
                value={formik.values.squareFootage}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.squareFootage &&
                  Boolean(formik.errors.squareFootage)
                }
                helperText={
                  formik.touched.squareFootage && formik.errors.squareFootage
                    ? String(formik.errors.squareFootage)
                    : ""
                }
                allowDecimal={false}
                sx={{ width: "calc(100% - 180px)" }}
              />
            </Box>
            <Divider />
            <Box className="user-name-layout">
              <Typography className="field-label-txt">O&M Required?</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.operationsAndMaintenanceRequired}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "operationsAndMaintenanceRequired",
                        e.target.checked
                      )
                    }
                    sx={{
                      color: "primary.main",
                      "&.Mui-checked": {
                        color: "primary.main",
                      },
                    }}
                  />
                }
                label={
                  formik.values.operationsAndMaintenanceRequired ? "Yes" : "No"
                }
                sx={{ width: "calc(100% - 180px)" }}
              />
            </Box>
            <Divider />
            <Box className="user-name-layout">
              <Typography className="field-label-txt">Loan Balance</Typography>
              <Box
                sx={{ display: "flex", gap: 2, width: "calc(100% - 180px)" }}
              >
                <InputNumber
                  fullWidth
                  placeholder="0.00"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  className="field-input"
                  name="loanBalance"
                  variant="outlined"
                  value={formik.values.loanBalance}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.loanBalance &&
                    Boolean(formik.errors.loanBalance)
                  }
                  sx={{ flex: 1 }}
                />
                <DatePicker
                  label="As of"
                  value={
                    formik.values.loanBalanceAsOf
                      ? dayjs(formik.values.loanBalanceAsOf)
                      : null
                  }
                  onChange={(date) => {
                    const dateValue = date?.format("YYYY-MM-DD");
                    formik.setFieldValue("loanBalanceAsOf", dateValue);
                    formik.setFieldTouched("loanBalanceAsOf", true);
                    setTimeout(() => {
                      formik.validateField("loanBalanceAsOf");
                    }, 0);
                  }}
                  format="MMMM D, YYYY"
                  slotProps={{
                    inputAdornment: {
                      position: "start",
                    },
                    textField: {
                      variant: "outlined",
                      placeholder: "Select Date",
                      error:
                        formik.touched.loanBalanceAsOf &&
                        Boolean(formik.errors.loanBalanceAsOf),
                      helperText:
                        formik.touched.loanBalanceAsOf &&
                        formik.errors.loanBalanceAsOf
                          ? String(formik.errors.loanBalanceAsOf)
                          : "",
                    },
                  }}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
        </>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="field-label-txt">Property Contact</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <Typography sx={{ mb: 1, fontWeight: 500 }}>Primary</Typography>
            {contacts.map((contact, index) => (
              <Box
                key={index}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  gridTemplateRows: "auto auto",
                  gap: 2,
                  mb: 2,
                  alignItems: "start",
                }}
              >
                <Box sx={{ gridRow: 1, gridColumn: 1, pt: 0.5 }}>
                  <Radio
                    sx={{ color: "primary.main", p: 0 }}
                    checked={selectedContact === `contact${index}`}
                    onChange={(e) => handleSelectedContactChange(e, index)}
                    value={`contact${index}`}
                    name="contact-radio"
                  />
                </Box>
                <Box sx={{ gridRow: 1, gridColumn: 2, minWidth: 0 }}>
                  <TextInput
                    className="field-input"
                    name={`contact_${index}_name`}
                    label="Name"
                    variant="outlined"
                    value={contact.name}
                    onChange={(e) =>
                      updateContact(index, "name", e.target.value)
                    }
                    onBlur={() => {
                      formik.setFieldTouched(`contacts.${index}.name`, true);
                      setTimeout(() => {
                        formik.validateField(`contacts.${index}.name`);
                      }, 0);
                    }}
                    error={Boolean(
                      (formik.touched.contacts?.[index] as any)?.name &&
                        (formik.errors.contacts?.[index] as any)?.name
                    )}
                    helperText={
                      (formik.touched.contacts?.[index] as any)?.name &&
                      (formik.errors.contacts?.[index] as any)?.name
                        ? String((formik.errors.contacts?.[index] as any)?.name)
                        : ""
                    }
                    sx={{ width: "100% !important" }}
                  />
                </Box>
                <Box
                  sx={{
                    gridRow: 1,
                    gridColumn: 3,
                    display: "flex",
                    justifyContent: "flex-end",
                    pt: 0.5,
                  }}
                >
                  {!contact.isPrimary ? (
                    <IconButton
                      onClick={() => removeContact(index)}
                      sx={{ color: "error.main", p: 0.5 }}
                      size="small"
                      aria-label="delete contact"
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  ) : null}
                </Box>
                <Box sx={{ gridRow: 2, gridColumn: 1 }} />
                <Box
                  sx={{
                    gridRow: 2,
                    gridColumn: 2,
                    display: "grid",
                    gridTemplateColumns: "2fr 1.5fr 1fr",
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  <TextInput
                    className="field-input"
                    name={`contact_${index}_email`}
                    label="Email"
                    variant="outlined"
                    type="email"
                    value={contact.email}
                    onChange={(e) =>
                      updateContact(index, "email", e.target.value)
                    }
                    onBlur={() => {
                      formik.setFieldTouched(`contacts.${index}.email`, true);
                      setTimeout(() => {
                        formik.validateField(`contacts.${index}.email`);
                      }, 0);
                    }}
                    error={Boolean(
                      (formik.touched.contacts?.[index] as any)?.email &&
                        (formik.errors.contacts?.[index] as any)?.email
                    )}
                    helperText={
                      (formik.touched.contacts?.[index] as any)?.email &&
                      (formik.errors.contacts?.[index] as any)?.email
                        ? String(
                            (formik.errors.contacts?.[index] as any)?.email
                          )
                        : ""
                    }
                    style={{ width: "100%" }}
                    sx={{ minWidth: 0 }}
                  />
                  <TextInput
                    className="field-input"
                    name={`contact_${index}_phone`}
                    label="Phone"
                    variant="outlined"
                    value={contact.phone}
                    onChange={(e) =>
                      updateContact(
                        index,
                        "phone",
                        phoneFormatInput(e.target.value)
                      )
                    }
                    onBlur={() => {
                      formik.setFieldTouched(`contacts.${index}.phone`, true);
                      setTimeout(() => {
                        formik.validateField(`contacts.${index}.phone`);
                      }, 0);
                    }}
                    error={
                      Boolean(
                        (formik.touched.contacts?.[index] as any)?.phone &&
                          (formik.errors.contacts?.[index] as any)?.phone
                      ) || Boolean(validatePhones()[index])
                    }
                    helperText={
                      (formik.touched.contacts?.[index] as any)?.phone &&
                      (formik.errors.contacts?.[index] as any)?.phone
                        ? String(
                            (formik.errors.contacts?.[index] as any)?.phone
                          )
                        : validatePhones()[index] || ""
                    }
                    style={{ width: "100%" }}
                    sx={{ minWidth: 0 }}
                  />
                  <FormControl sx={{ minWidth: 0 }}>
                    <InputLabel
                      id={`property-contact-phone-type-full-label-${index}`}
                      shrink
                    >
                      Phone Type
                    </InputLabel>
                    <Select
                      displayEmpty
                      labelId={`property-contact-phone-type-full-label-${index}`}
                      value={contact.phoneType ?? ""}
                      onChange={(e) =>
                        updateContact(index, "phoneType", e.target.value)
                      }
                      onBlur={() => {
                        formik.setFieldTouched(
                          `contacts.${index}.phoneType`,
                          true
                        );
                        setTimeout(() => {
                          formik.validateField(`contacts.${index}.phoneType`);
                        }, 0);
                      }}
                      label="Phone Type"
                    >
                      <MenuItem value="">
                        <em>Select</em>
                      </MenuItem>
                      <MenuItem value="Mobile">Mobile</MenuItem>
                      <MenuItem value="Office">Office</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ gridRow: 2, gridColumn: 3 }} />
              </Box>
            ))}
            {contacts.length < 4 && (
              <Button
                startIcon={<Add />}
                onClick={addContact}
                sx={{
                  color: "primary.main",
                  textTransform: "none",
                  fontWeight: 600,
                  mt: 2,
                  p: 0,
                  "&:hover": { backgroundColor: "transparent" },
                }}
              >
                + ADD ADDITIONAL
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
